import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Heart, Users, HandHeart, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const HOPE = {
  hope1: 'https://customer-assets.emergentagent.com/job_ktm-2026/artifacts/uutcmrrs_hope1.png',
  hope2: 'https://customer-assets.emergentagent.com/job_ktm-2026/artifacts/u9lg1e4m_hope2.png',
  hope3: 'https://customer-assets.emergentagent.com/job_ktm-2026/artifacts/xdoh0scu_hope3.png',
  hope4: 'https://customer-assets.emergentagent.com/job_ktm-2026/artifacts/goow86oc_hope4.png',
  hope5: 'https://customer-assets.emergentagent.com/job_ktm-2026/artifacts/j054gm1t_IMG_8382.webp',
  hope6: 'https://customer-assets.emergentagent.com/job_ktm-2026/artifacts/g93x2211_hope6.png',
  hope7: 'https://customer-assets.emergentagent.com/job_ktm-2026/artifacts/tx723i32_hope7.png',
};

export const Outreach = () => {
  return (
    <div className="min-h-screen" data-testid="outreach-page">
      {/* Hero */}
      <section className="relative pt-56 md:pt-60 pb-20 bg-ktm-navy text-white overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 25% 25%, white 1px, transparent 1px), radial-gradient(circle at 75% 75%, white 1px, transparent 1px)', backgroundSize: '50px 50px' }} />
        <div className="absolute -top-20 -right-20 w-96 h-96 bg-ktm-red/10 blur-3xl pointer-events-none" />
        
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            className="max-w-3xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <div className="inline-flex items-center gap-2 mb-6 px-4 py-1.5 border border-ktm-red/40 bg-ktm-red/10">
              <Heart className="w-4 h-4 text-ktm-red" aria-hidden="true" />
              <span className="text-ktm-red font-bold uppercase tracking-[0.2em] text-xs">Community Outreach</span>
            </div>
            <h1 className="font-chivo font-black text-4xl md:text-6xl leading-[0.95] mb-6">
              Building More Than Buildings.<br />
              <span className="text-ktm-red">Building Hope.</span>
            </h1>
            <p className="text-slate-300 text-base md:text-lg leading-relaxed max-w-2xl">
              KTM is proud to support organizations that make a meaningful difference in our community.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story + Feature image */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <motion.div
              className="lg:col-span-6 order-2 lg:order-1 space-y-6"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-ktm-red font-bold uppercase tracking-wider text-sm">A Signature Community Project</span>
              <h2 className="font-chivo font-black text-3xl md:text-4xl text-ktm-navy leading-tight">
                Interior Construction for the New X Hope Building
              </h2>
              <p className="text-slate-700 text-base md:text-lg leading-relaxed">
                As part of its commitment to giving back, KTM provided the interior construction for the new
                <span className="font-bold text-ktm-navy"> X Hope </span>
                building in Elk Grove, California.
              </p>
              <p className="text-slate-700 text-base md:text-lg leading-relaxed">
                X Hope is dedicated to empowering vulnerable children and families by providing hope, love, education, and support. We are honored to contribute to their mission and help create a space where they can continue making a lasting impact on the lives of those they serve.
              </p>
              <div className="flex items-center gap-4 pt-4 border-t border-slate-200">
                <div className="w-12 h-12 bg-ktm-red/10 flex items-center justify-center">
                  <HandHeart className="w-6 h-6 text-ktm-red" aria-hidden="true" />
                </div>
                <div>
                  <div className="font-chivo font-bold text-ktm-navy">Elk Grove, California</div>
                  <div className="text-sm text-slate-500">Pathways of Hope Empowerment Center</div>
                </div>
              </div>
            </motion.div>

            <motion.div
              className="lg:col-span-6 order-1 lg:order-2 relative"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
            >
              <div className="aspect-[4/5] overflow-hidden border-4 border-ktm-navy shadow-2xl">
                <img
                  src={HOPE.hope1}
                  alt="KTM team receiving X Hope recognition plaque"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -z-10 top-6 -right-6 w-full h-full border-4 border-ktm-red/40" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Belief statement */}
      <section className="py-20 bg-ktm-muted">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="w-16 h-16 bg-ktm-red mx-auto flex items-center justify-center mb-8">
              <Users className="w-8 h-8 text-white" aria-hidden="true" />
            </div>
            <p className="font-chivo font-bold text-2xl md:text-3xl text-ktm-navy leading-snug italic">
              &ldquo;At KTM, we believe that building stronger communities goes beyond construction — it&apos;s about investing in people and making a positive difference where it matters most.&rdquo;
            </p>
          </motion.div>
        </div>
      </section>

      {/* Photo gallery */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-ktm-red font-bold uppercase tracking-wider text-sm">Moments From The Project</span>
            <h2 className="font-chivo font-black text-3xl md:text-4xl text-ktm-navy mt-4">
              Celebrating X Hope Together
            </h2>
          </motion.div>

          <div className="grid grid-cols-12 gap-4">
            <motion.div
              className="col-span-12 md:col-span-8 relative overflow-hidden group"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
            >
              <div className="aspect-[16/9] overflow-hidden">
                <img src={HOPE.hope4} alt="Ribbon cutting ceremony at X Hope" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
            </motion.div>

            <motion.div
              className="col-span-6 md:col-span-4 relative overflow-hidden group"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="aspect-[9/16] md:aspect-auto md:h-full overflow-hidden">
                <img src={HOPE.hope2} alt="KTM team member with X Hope plaque" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
            </motion.div>

            <motion.div
              className="col-span-6 md:col-span-4 relative overflow-hidden group"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <div className="aspect-square overflow-hidden">
                <img src={HOPE.hope3} alt="KTM team with X Hope director" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
            </motion.div>

            <motion.div
              className="col-span-12 md:col-span-8 relative overflow-hidden group"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <div className="aspect-[16/9] overflow-hidden">
                <img src={HOPE.hope5} alt="Presentation and speech at X Hope opening" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
            </motion.div>

            <motion.div
              className="col-span-6 md:col-span-6 relative overflow-hidden group"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img src={HOPE.hope6} alt="Completed X Hope interior with sun mural" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
            </motion.div>

            <motion.div
              className="col-span-6 md:col-span-6 relative overflow-hidden group"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img src={HOPE.hope7} alt="X Hope retail space serving the community" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-ktm-red">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-chivo font-black text-3xl md:text-4xl text-white mb-6">
              Have a Community Project?
            </h2>
            <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
              We&apos;d love to hear about it. Let&apos;s talk about how KTM can help bring your mission to life.
            </p>
            <Link to="/contact" data-testid="outreach-contact-btn">
              <Button 
                className="bg-white text-ktm-navy h-14 px-8 rounded-none font-bold uppercase tracking-wider hover:bg-slate-100"
              >
                Get in Touch
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden="true" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Outreach;
